import { inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationSkipped,
  NavigationStart,
  Router,
} from '@angular/router';
import { LoaderService } from '@core/services/loader.service';
import { SnackbarService } from '@core/services/snackbar.service';

/**
 * Espera antes de mostrar el loader. Las navegaciones a módulos ya descargados
 * resuelven en pocos milisegundos; sin esta espera el loader parpadearía en cada
 * click del menú.
 */
const SHOW_LOADER_DELAY_MS = 180;

/**
 * Tope de seguridad del loader. Si una navegación nunca termina (por ejemplo un
 * guard cuyo observable no emite), el loader se oculta igual para no dejar al
 * usuario bloqueado sin salida: es preferible un menú que no responde a una
 * pantalla congelada de la que no se puede salir.
 */
const LOADER_SAFETY_TIMEOUT_MS = 15000;

/** Mensajes con los que el navegador reporta un chunk lazy que no se pudo descargar. */
const CHUNK_LOAD_ERROR_PATTERN =
  /failed to fetch dynamically imported module|importing a module script failed|error loading dynamically imported module|chunkloaderror/i;

/** Prefijo de las marcas en `sessionStorage` que evitan recargas en bucle. */
const RECOVERY_FLAG_PREFIX = 'nf:chunk-recovery:';

const SNACKBAR_CLOSE_LABEL = 'Cerrar';

/**
 * @description
 * Conecta los eventos del router con el loader global y recupera la navegación
 * cuando falla la descarga de un chunk lazy.
 *
 * El caso real que motiva esto: al desplegar, `outputHashing` renombra los chunks
 * y `swa deploy` reemplaza los archivos. Una pestaña abierta desde antes conserva
 * el `index.html` viejo y sigue pidiendo nombres de chunk que ya no existen, así
 * que el `import()` de la ruta responde 404. Angular aborta la navegación en
 * silencio: la URL no cambia y el click del menú "no hace nada", de forma
 * permanente para ese módulo porque el navegador cachea el módulo como fallido.
 *
 * La recuperación es una carga completa hacia la URL destino, que trae el
 * `index.html` nuevo con los nombres de chunk vigentes y deja al usuario en la
 * vista que pidió. Se hace una sola vez por URL (marca en `sessionStorage`) para
 * que un 404 permanente no genere un bucle de recargas.
 */
@Injectable({ providedIn: 'root' })
export class RouterNavigationFeedbackService {
  private readonly router = inject(Router);
  private readonly loader = inject(LoaderService);
  private readonly snackbar = inject(SnackbarService);

  private showLoaderTimer?: ReturnType<typeof setTimeout>;
  private safetyTimer?: ReturnType<typeof setTimeout>;

  constructor() {
    this.router.events.pipe(takeUntilDestroyed()).subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.scheduleLoader();
        return;
      }

      if (
        event instanceof NavigationEnd ||
        event instanceof NavigationSkipped ||
        event instanceof NavigationCancel ||
        event instanceof NavigationError
      ) {
        this.hideLoader();
      }

      if (event instanceof NavigationEnd) {
        // La ruta volvió a cargar bien: se libera la marca para que un fallo
        // futuro (otro despliegue) pueda recuperarse de nuevo.
        this.clearRecoveryFlag(event.urlAfterRedirects);
        return;
      }

      if (event instanceof NavigationError) {
        this.reportNavigationError(event);
        return;
      }

      // `NavigationCancel` es normal cuando un guard redirige o cuando un click
      // nuevo reemplaza una navegación en curso; se registra solo como traza.
      if (event instanceof NavigationCancel) {
        console.warn(`[router] Navegación cancelada hacia "${event.url}": ${event.reason}`);
      }
    });
  }

  /**
   * @description
   * Programa el loader tras `SHOW_LOADER_DELAY_MS` y arma el tope de seguridad.
   * @returns {void}
   */
  private scheduleLoader(): void {
    this.clearTimers();

    this.showLoaderTimer = setTimeout(() => {
      this.loader.open();

      this.safetyTimer = setTimeout(() => {
        this.loader.close();
        console.warn(
          '[router] La navegación superó el tope de seguridad; se oculta el loader para no bloquear la app.',
        );
      }, LOADER_SAFETY_TIMEOUT_MS);
    }, SHOW_LOADER_DELAY_MS);
  }

  /**
   * @description
   * Oculta el loader y cancela los temporizadores pendientes.
   * @returns {void}
   */
  private hideLoader(): void {
    this.clearTimers();
    this.loader.close();
  }

  private clearTimers(): void {
    clearTimeout(this.showLoaderTimer);
    clearTimeout(this.safetyTimer);
    this.showLoaderTimer = undefined;
    this.safetyTimer = undefined;
  }

  /**
   * @description
   * Recupera la navegación fallida o, si no es recuperable, avisa al usuario.
   * @param {NavigationError} event Evento de navegación fallida.
   * @returns {void}
   */
  private reportNavigationError(event: NavigationError): void {
    console.error(`[router] Falló la navegación hacia "${event.url}"`, event.error);

    if (this.isChunkLoadError(event.error) && this.tryRecoverStaleBuild(event.url)) {
      return;
    }

    const message = this.isChunkLoadError(event.error)
      ? 'No se pudo cargar esta sección. Recarga la página para reintentar.'
      : 'No se pudo abrir la sección solicitada.';

    this.snackbar.open(message, SNACKBAR_CLOSE_LABEL, {
      duration: 8000,
      panelClass: 'snackbar-error',
    });
  }

  /**
   * @description
   * Fuerza una carga completa hacia la URL destino para traer el `index.html`
   * vigente. Solo se intenta una vez por URL.
   * @param {string} url URL destino de la navegación que falló.
   * @returns {boolean} `true` si se lanzó la recuperación.
   */
  private tryRecoverStaleBuild(url: string): boolean {
    if (this.hasRecoveryFlag(url)) {
      console.error(
        `[router] El chunk de "${url}" sigue fallando tras recargar; no se reintenta para evitar un bucle.`,
      );
      return false;
    }

    this.setRecoveryFlag(url);
    console.warn(`[router] Chunk no disponible (build desactualizado). Recargando "${url}".`);
    location.assign(url);
    return true;
  }

  private isChunkLoadError(error: unknown): boolean {
    const message = error instanceof Error ? error.message : String(error ?? '');
    return CHUNK_LOAD_ERROR_PATTERN.test(message);
  }

  // `sessionStorage` puede lanzar (modo privado, cookies bloqueadas). Si no está
  // disponible se pierde la protección anti-bucle, no la navegación.
  private hasRecoveryFlag(url: string): boolean {
    try {
      return sessionStorage.getItem(RECOVERY_FLAG_PREFIX + url) === '1';
    } catch {
      return false;
    }
  }

  private setRecoveryFlag(url: string): void {
    try {
      sessionStorage.setItem(RECOVERY_FLAG_PREFIX + url, '1');
    } catch {
      /* sin almacenamiento no se puede marcar; se acepta el riesgo de un reintento extra */
    }
  }

  private clearRecoveryFlag(url: string): void {
    try {
      sessionStorage.removeItem(RECOVERY_FLAG_PREFIX + url);
    } catch {
      /* nada que limpiar si no hay almacenamiento */
    }
  }
}
